/**
 * 表单附件上传控件（`upload` 字段 · C-23 / D-06 多份）
 * -----------------------------------------------------------------------------
 * 值形状（三端同一口径，见 `shared/form-validator.ts`）：
 *   - 单份字段（`multiple !== true`）→ **assetId 字符串**
 *   - 多份字段（`multiple === true`）→ **assetId 数组**，顺序 = 上传顺序
 *
 * 为什么单份字段不能收数组、多份字段也不用「统一成数组」：同一条 `formData` 里
 * 同一个 key 一会儿字符串一会儿数组，所有消费方（材料清单 / 确认页 / 导出 / 后台）
 * 都得写两套分支，长期一定有一处漏掉。形状由字段声明决定，消费方只看声明。
 *
 * 预览用**本地 objectURL**（上传成功后即刻可看，不需要再向服务端要签名链接）；
 * 重开草稿时本地预览丢失 → 显示「已上传」占位 + 可重新上传。
 * 已上传资产的读取走 `GET /api/files/{key}` 签名链，本控件不承担查看职责。
 *
 * 上传是**逐张串行**的：并行发 5 个请求既容易被服务端限流，也会让「第 3 张失败」
 * 的提示顺序和网格顺序对不上。每成功一张就立刻同步一次父级值 ——
 * 中途失败 / 用户提前退出时，已经传上去的那几张不会白传。
 */
import { Dialog } from 'antd-mobile';
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../../api/client';
import { uploadFormAsset } from '../../api/h5';
import { Button } from '../layout';

export interface UploadFieldProps {
  /** 当前值：单份 = assetId 字符串；多份 = assetId 数组（undefined / 空数组 = 未上传） */
  value: unknown;
  disabled: boolean;
  /** 归属的报名单：带上它，提交时服务端才能删掉被替换掉的旧附件 */
  registrationId?: string;
  /** 是否允许多份（`field.multiple`） */
  multiple?: boolean;
  /** 允许多份时的有效上限（已按 `uploadMaxCount()` 换算过，单份字段传 1 即可） */
  maxCount?: number;
  onChange: (next: string | string[] | undefined) => void;
  /** 失焦：把刚落库的 assetId 带上去，父级据此校验新值 */
  onBlur: (next?: unknown) => void;
}

/** 一条已上传附件：`url` 只对**本次会话里刚传的**有值（草稿回填的没有本地预览） */
interface Entry {
  assetId: string;
  url: string | null;
  /** 非图片（PDF）：有 url 也不能塞进 `<img>` */
  pdf: boolean;
}

function toEntries(value: unknown, multiple: boolean): string[] {
  const raw = Array.isArray(value) ? value : [value];
  const ids: string[] = [];
  for (const item of raw) {
    if (typeof item === 'string' && item.trim() !== '') ids.push(item.trim());
  }
  // 单份字段只认第一个：形状不合法由校验器报错，这里只需保证回显不炸
  return multiple ? ids : ids.slice(0, 1);
}

function sameIds(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

export function UploadField({
  value,
  disabled,
  registrationId,
  multiple = false,
  maxCount = 1,
  onChange,
  onBlur,
}: UploadFieldProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [entries, setEntries] = useState<Entry[]>(() =>
    toEntries(value, multiple).map((assetId) => ({ assetId, url: null, pdf: false })),
  );
  const [busy, setBusy] = useState(false);

  const limit = multiple ? Math.max(1, maxCount) : 1;
  const valueIds = toEntries(value, multiple);

  // 外部改了值（草稿回填 / 表单重置 / 服务端回吐）→ 网格跟着变。
  // 已经存在的项保留本地预览，别因为父级重渲染就把缩略图抹成「已上传」。
  useEffect(() => {
    setEntries((prev) => {
      if (
        sameIds(
          prev.map((entry) => entry.assetId),
          valueIds,
        )
      )
        return prev;
      const byId = new Map(prev.map((entry) => [entry.assetId, entry]));
      return valueIds.map((assetId) => byId.get(assetId) ?? { assetId, url: null, pdf: false });
    });
  }, [valueIds]);

  // 卸载时统一回收：被替换 / 被删的预览 URL 到这一步才确定没人用了
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  useEffect(() => {
    return () => {
      for (const entry of entriesRef.current) {
        if (entry.url) URL.revokeObjectURL(entry.url);
      }
    };
  }, []);

  const pick = () => inputRef.current?.click();

  const emit = (next: readonly Entry[]) => {
    const ids = next.map((entry) => entry.assetId);
    const payload = ids.length === 0 ? undefined : multiple ? ids : ids[0];
    onChange(payload);
    return payload;
  };

  const handleFiles = async (files: readonly File[]) => {
    if (files.length === 0) return;

    // 单份字段：选中的文件**替换**现有值（「重新上传」的语义）——
    // 不能走下面的「剩余额度」逻辑，否则额度恒为 1、有值时永远提示「已达上限」
    if (!multiple) {
      const file = files[0];
      if (!file) return;
      setBusy(true);
      try {
        const result = await uploadFormAsset(
          file,
          file.name || 'attachment',
          'FORM_ATTACHMENT',
          registrationId,
        );
        const previous = entries[0];
        if (previous?.url) URL.revokeObjectURL(previous.url);
        const next: Entry[] = [
          {
            assetId: result.assetId,
            url: URL.createObjectURL(file),
            pdf: file.type === 'application/pdf' || /\.pdf$/i.test(file.name),
          },
        ];
        setEntries(next);
        onChange(result.assetId);
        onBlur(result.assetId);
      } catch (error) {
        Dialog.alert({
          title: '上传失败',
          content: error instanceof ApiError ? error.message : '上传失败，请重试',
        });
      } finally {
        setBusy(false);
      }
      return;
    }

    const remaining = limit - entries.length;
    if (remaining <= 0) {
      Dialog.alert({ title: '已达上限', content: `最多上传 ${limit} 份，请先删除已有附件` });
      return;
    }
    if (files.length > remaining) {
      // 明确告诉用户「多选了几个、实际收几个」——静默截断会让人以为全都传上去了
      Dialog.alert({
        title: '超出数量上限',
        content: `最多上传 ${limit} 份，本次只上传前 ${remaining} 个文件`,
      });
    }

    setBusy(true);
    const current = [...entries];
    try {
      for (const file of files.slice(0, remaining)) {
        try {
          const result = await uploadFormAsset(
            file,
            file.name || 'attachment',
            'FORM_ATTACHMENT',
            registrationId,
          );
          current.push({
            assetId: result.assetId,
            url: URL.createObjectURL(file),
            pdf: file.type === 'application/pdf' || /\.pdf$/i.test(file.name),
          });
          setEntries([...current]);
          emit(current);
        } catch (error) {
          // 停在这一张：继续往下传只会再弹几个同样的框，用户还得自己数传到第几个了
          Dialog.alert({
            title: '上传失败',
            content: error instanceof ApiError ? error.message : '上传失败，请重试',
          });
          break;
        }
      }
    } finally {
      setBusy(false);
      if (current.length > entries.length) onBlur(current.map((entry) => entry.assetId));
    }
  };

  const removeOne = (assetId: string) => {
    const next = entries.filter((entry) => entry.assetId !== assetId);
    const removed = entries.find((entry) => entry.assetId === assetId);
    if (removed?.url) URL.revokeObjectURL(removed.url);
    setEntries(next);
    const payload = emit(next);
    onBlur(payload);
  };

  const full = entries.length >= limit;

  return (
    <div className="upload-field">
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept="image/jpeg,image/png,image/webp,application/pdf"
        className="upload-field__input"
        disabled={disabled || busy}
        onChange={(event) => {
          const picked = Array.from(event.target.files ?? []);
          // 先清 value，再选同一批文件也能触发 change
          event.target.value = '';
          if (picked.length > 0) void handleFiles(picked);
        }}
      />

      {multiple ? (
        <>
          <div className="upload-grid">
            {entries.map((entry, index) => (
              <div key={entry.assetId} className="upload-item">
                {entry.url && !entry.pdf ? (
                  <img
                    src={entry.url}
                    alt={`已上传附件 ${index + 1}`}
                    className="upload-item__thumb"
                  />
                ) : (
                  <div className="upload-item__placeholder">
                    {entry.pdf ? 'PDF' : '已上传'}
                    <span className="upload-item__index">第 {index + 1} 份</span>
                  </div>
                )}
                <button
                  type="button"
                  className="upload-item__remove"
                  aria-label={`删除第 ${index + 1} 份附件`}
                  disabled={disabled || busy}
                  onClick={() => removeOne(entry.assetId)}
                >
                  ×
                </button>
              </div>
            ))}
            {full ? null : (
              <button
                type="button"
                className="upload-item upload-item--add"
                disabled={disabled || busy}
                onClick={pick}
              >
                <span className="upload-item__plus">{busy ? '…' : '+'}</span>
                <span className="upload-item__hint">{busy ? '上传中' : '添加'}</span>
              </button>
            )}
          </div>
          <div className="upload-field__actions">
            <span className="upload-field__badge">
              已上传 {entries.length} / {limit} 份（支持 PDF，单个文件不超过 10MB）
            </span>
          </div>
        </>
      ) : (
        <>
          {entries[0]?.url && !entries[0].pdf ? (
            <img src={entries[0].url} alt="已上传附件预览" className="upload-field__preview" />
          ) : entries.length > 0 ? (
            <div className="upload-field__badge">
              {entries[0]?.pdf ? '已上传 PDF' : '已上传'}（重新上传可替换）
            </div>
          ) : null}
          <div className="upload-field__actions">
            <Button
              variant="ghost"
              size="sm"
              disabled={disabled || busy}
              loading={busy}
              onClick={pick}
            >
              {entries.length > 0 ? '重新上传' : '选择图片 / PDF'}
            </Button>
            {entries.length > 0 ? (
              <Button
                variant="text"
                size="sm"
                disabled={disabled || busy}
                onClick={() => removeOne(entries[0]?.assetId ?? '')}
              >
                移除
              </Button>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
